/**
 * My offline PIN (D20): whether one is set and since when; set or change it
 * (typed twice, at least 6 characters), or clear it. Needs `local.login`
 * somewhere; without it the card says why and offers only clearing an old
 * one. The PIN works on a node only while the node cannot reach the cloud.
 */
import { offlinePinSchema } from '@acp/api-contract/offlineAuth.js';
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';

export type OfflinePinCardProps = {
  readonly status: { readonly set: boolean; readonly updatedAt: string | null } | null;
  readonly canSet: boolean;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSet: (pin: string) => void;
  readonly onClear: () => void;
};

function PinForm({ isSet, pending, onSet }: { readonly isSet: boolean; readonly pending: boolean; readonly onSet: (pin: string) => void }) {
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const valid = offlinePinSchema.safeParse(pin).success;
  const matches = pin === again;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid || !matches || pending) return;
    onSet(pin);
    setPin('');
    setAgain('');
  };
  return (
    <form className="grid max-w-sm gap-3" onSubmit={submit}>
      <div className="grid gap-1">
        <Label htmlFor="pin-new">{isSet ? 'New PIN' : 'PIN'}</Label>
        <Input id="pin-new" type="password" inputMode="numeric" autoComplete="new-password" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="pin-new" />
      </div>
      <div className="grid gap-1">
        <Label htmlFor="pin-again">Repeat it</Label>
        <Input id="pin-again" type="password" inputMode="numeric" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} data-testid="pin-again" />
      </div>
      {pin !== '' && !valid ? <p className="text-sm text-destructive">At least 6 characters.</p> : null}
      {again !== '' && !matches ? <p className="text-sm text-destructive">The two do not match.</p> : null}
      <Button type="submit" className="w-fit" disabled={!valid || !matches || pending} data-testid="pin-save">
        {pending ? 'Saving…' : isSet ? 'Change PIN' : 'Set PIN'}
      </Button>
    </form>
  );
}

function statusText(status: OfflinePinCardProps['status']): string {
  if (status === null) return 'Loading…';
  return status.set ? `A PIN is set (since ${status.updatedAt?.slice(0, 10) ?? '?'}).` : 'No PIN set.';
}

export function OfflinePinCard({ status, canSet, pending, error, onSet, onClear }: OfflinePinCardProps) {
  const isSet = status?.set === true;
  return (
    <Card data-testid="offline-pin">
      <CardHeader>
        <CardTitle>Offline PIN</CardTitle>
        <CardDescription>Signs you in on a studio server while it cannot reach the cloud. Every use is audited. Longer is stronger.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <p className="text-sm" data-testid="pin-status">
          {statusText(status)}
        </p>
        {canSet ? (
          <PinForm isSet={isSet} pending={pending} onSet={onSet} />
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="pin-not-allowed">
            Your groups do not include offline sign-in (local.login), so you cannot set a PIN. Ask an administrator if you need one.
          </p>
        )}
        {error === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {isSet ? (
          <Button variant="outline" className="w-fit" disabled={pending} onClick={onClear} data-testid="pin-clear">
            Clear my PIN
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
